import type { EntityType, RoleLevel } from "@/lib/shared/constants";

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  userInfo?: UserInfo;
  mfaRequired?: boolean;
  mfaFactors?: MFAFactor[];
}

// MFA Types
export interface MFAFactor {
  id: string;
  type: string;
  friendlyName: string;
  status?: string;
  createdAt?: string;
}

export interface MFAEnrollResponse {
  id: string;
  type: string;
  totp_uri: string;
  secret: string;
  qr_code?: string;
}

export interface MFAChallengeResponse {
  id: string;
  expiresAt: number;
}

export interface MFAVerifyRequest {
  factorId: string;
  challengeId: string;
  code: string;
}

export interface MFAVerifyResponse {
  accessToken: string;
  refreshToken: string;
  user: UserInfo;
}

export interface MFAStatusResponse {
  enabled: boolean;
  required: boolean;
  verifiedAt?: string;
  factors: MFAFactor[];
}

// Magic Link Types
export interface MagicLinkRequestResponse {
  message: string;
}

export interface MagicLinkVerifyResponse {
  token: string;
  refreshToken: string;
  user?: UserInfo;
  mfaRequired?: boolean;
  mfaFactors?: MFAFactor[];
}

// Passkey (WebAuthn) Types
export interface Passkey {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt?: string;
}

export interface PasskeyRegistrationOptionsResponse {
  challengeId: string;
  options: PublicKeyCredentialCreationOptions;
}

export interface PasskeyRegistrationVerifyResponse {
  message: string;
  passkey: Passkey;
}

export interface PasskeyLoginOptionsResponse {
  challengeId: string;
  options: PublicKeyCredentialRequestOptions;
}

export interface PasskeyLoginVerifyResponse {
  token: string;
  refreshToken: string;
  user: UserInfo;
}

export interface LoginRedirectResponse {
  redirect: string;
  message: string;
}

// Custom error class for domain redirects
export class DomainRedirectError extends Error {
  redirectUrl: string;

  constructor(redirectUrl: string, message: string) {
    super(message);
    this.name = 'DomainRedirectError';
    this.redirectUrl = redirectUrl;
  }
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  token: string;
  refreshToken: string;
  scope: string;
}

export interface UpdateUserRequest {
  id: string;
  email: string;
  fullName?: string;
  role?: RoleLevel;
  organizationId?: string;
  integratorId?: string;
}

/**
 * User information returned from the API
 */
export interface UserInfo {
  id: string;
  email: string;
  fullName?: string;
  role?: RoleLevel;                    // owner, admin, editor, viewer
  entityType?: EntityType;             // system, integrator, organization
  organizationId?: string;
  integratorId?: string;
}

export interface AuthUser {
  token: string;
  refreshToken: string;
  userInfo?: UserInfo;
}

export interface AuthStore {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: (user: AuthUser) => void;
  logout: () => Promise<void>;
  updateUserInfo: (userInfo: UserInfo) => void;
  updateTokens: (tokens: { token: string; refreshToken: string }) => void;
  refreshToken: () => Promise<boolean>;
  initializeAuth: () => void;
  startTokenRefreshMonitoring: () => void;
  stopTokenRefreshMonitoring: () => void;
}
